<script setup>
import { ref, onMounted } from 'vue';
import { useScrollAnimations } from '@/composables/useScrollAnimations';

const { animateInView } = useScrollAnimations();
const graphRef = ref(null);
const line1Ref = ref(null);
const line2Ref = ref(null);

onMounted(() => {
  if (graphRef.value) {
    animateInView(graphRef.value, () => {
      if (line1Ref.value) line1Ref.value.classList.add('animate-draw');
      if (line2Ref.value) line2Ref.value.classList.add('animate-draw-delayed');
    }, 0.5);
  }
});
</script>

<template>
  <div
    class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center"
    ref="graphRef"
  >
    <!-- Text Side -->
    <div class="space-y-6 text-left">
      <h3 class="text-2xl font-bold text-white">
        {{ $t('landingPage.v2.comparison.graph.title') }}
      </h3>
      <div class="space-y-4 text-gray-400">
        <p>{{ $t('landingPage.v2.comparison.graph.description1') }}</p>
        <p>{{ $t('landingPage.v2.comparison.graph.description2') }}</p>
        <p>{{ $t('landingPage.v2.comparison.graph.description3') }}</p>
        <p>{{ $t('landingPage.v2.comparison.graph.description4') }}</p>
      </div>
    </div>

    <!-- Graph Side -->
    <div class="relative bg-black/40 border border-white/10 rounded-2xl p-8 aspect-[4/3] flex items-end">
      <!-- Labels -->
      <!-- Fixed Y Scale Label -->
      <div
        class="absolute top-1/2 -left-24 -translate-y-1/2 -rotate-90 text-gray-400 text-2xs tracking-widest whitespace-nowrap"
        style="transform-origin: center center;"
      >
        {{ $t('landingPage.v2.comparison.graph.axisY') }}
      </div>

      <div class="absolute bottom-4 left-1/2 -translate-x-1/2 text-gray-400 text-sm tracking-widest">
        {{
          $t('landingPage.v2.comparison.graph.axisX') }}
      </div>

      <!-- Chart Area -->
      <svg
        class="w-full h-full overflow-visible"
        viewBox="0 0 400 300"
        preserveAspectRatio="none"
      >
        <!-- Axes -->
        <line
          x1="40"
          y1="260"
          x2="380"
          y2="260"
          stroke="#4B5563"
          stroke-width="2"
          marker-end="url(#arrow)"
        />
        <!-- X Axis -->
        <line
          x1="40"
          y1="260"
          x2="40"
          y2="40"
          stroke="#4B5563"
          stroke-width="2"
          marker-end="url(#arrow)"
        />
        <!-- Y Axis -->

        <defs>
          <marker
            id="arrow"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path
              d="M0,0 L0,6 L9,3 z"
              fill="#4B5563"
            />
          </marker>
        </defs>

        <!-- Curves -->
        <!-- M startX startY Q controlX controlY endX endY -->
        <path
          ref="line1Ref"
          d="M 40 260 Q 200 240 380 60"
          fill="none"
          stroke="#2CA58D"
          stroke-width="3"
          stroke-linecap="round"
          class="path-line"
        />
        <path
          ref="line2Ref"
          d="M 40 260 Q 200 260 380 230"
          fill="none"
          stroke="#DB2777"
          stroke-width="3"
          stroke-linecap="round"
          class="path-line"
        />

        <!-- Labels on lines -->
        <text
          x="200"
          y="100"
          fill="white"
          font-size="12"
          font-weight="bold"
          class="opacity-0 animate-fade-in delay-1000"
        >{{
          $t('landingPage.v2.comparison.graph.labels.cyberalarm') }}</text>
        <text
          x="250"
          y="200"
          fill="white"
          font-size="12"
          font-weight="bold"
          class="opacity-0 animate-fade-in delay-1000"
        >{{ $t('landingPage.v2.comparison.graph.labels.others')
        }}</text>

      </svg>
    </div>
  </div>
</template>

<style scoped>
.path-line {
    stroke-dasharray: 1000;
    stroke-dashoffset: 1000;
}

.animate-draw {
    animation: draw 2s ease-out forwards;
}

.animate-draw-delayed {
    animation: draw 2s ease-out 0.5s forwards;
}

.animate-fade-in {
    animation: fadeIn 1s ease-out forwards;
}

.delay-1000 {
    animation-delay: 2s;
    /* Wait for line to draw approx */
}

@keyframes draw {
    to {
        stroke-dashoffset: 0;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}
</style>
